import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseUrlFiltersOptions<T> {
  parse: (params: URLSearchParams) => T
}

export function useUrlFilters<T extends Record<string, any>>(
  options: UseUrlFiltersOptions<T>
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => options.parse(searchParams), [searchParams])

  const updateFilters = useCallback((updates: Partial<T>) => {
    const newParams = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value))
      } else {
        newParams.delete(key)
      }
    })
    if (!('page' in updates)) newParams.set('page', '1')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const replaceFilters = useCallback((next: T) => {
    const newParams = new URLSearchParams()
    Object.entries(next).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value))
      }
    })
    setSearchParams(newParams)
  }, [setSearchParams])

  return { filters, updateFilters, replaceFilters }
}
